<template>
  <div class="finance-orders-page">
    <search-finance-orders @submit="submitSearch" />

    <div class="simiot-table-operation-buttons">
      <a-space>
        <finance-orders-batch-status
          v-if="isShowMenu('order_summary/finance_orders/payment') || isShowMenu('order_summary/finance_orders/invoice')"
          :selected-row-keys="selectedRowKeys"
          @completed="fetchData"
        />
        <a-button
          v-if="isShowMenu('order_summary/finance_orders/export')"
          type="primary"
          @click="exportData"
        >
          导出
        </a-button>
      </a-space>
    </div>

    <div class="finance-orders-table-wrap">
      <a-table
        size="middle"
        :scroll="{ x: 1360 }"
        :columns="columns"
        :data-source="data"
        :loading="loading"
        :pagination="false"
        :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
        :expanded-row-keys="expandedRowKeys"
        :expand-row-by-click="false"
        :expand-icon="expandIcon"
        row-key="id"
        @expand="onExpand"
      >
        <template slot="expandedRowRender" slot-scope="record">
          <div v-if="record.detailLoading" class="finance-order-detail finance-order-detail--loading">
            <a-spin size="small" />
            <span class="finance-order-detail__loading-text">加载详情中…</span>
          </div>
          <div v-else-if="record.detail" class="finance-order-detail">
            <template v-if="visibleDetailRows(record.detail).length">
              <div
                v-for="row in visibleDetailRows(record.detail)"
                :key="row.label"
                class="finance-order-detail__row"
              >
                <span class="finance-order-detail__label">{{ row.label }}：</span>
                <span class="finance-order-detail__value">
                  <a
                    v-if="row.purchaseItemId"
                    @click="openPurchaseItemDetail(row.purchaseItemId)"
                    class="finance-order-detail__link"
                  >
                    {{ row.value }}
                  </a>

                  <router-link
                    v-else-if="row.link"
                    :to="row.link"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="finance-order-detail__link"
                  >
                    {{ row.value }}
                  </router-link>

                  <template v-else>{{ row.value }}</template>
                </span>
              </div>
            </template>
            <div v-else class="finance-order-detail--hint finance-order-detail--hint-only">
              暂无详情数据
            </div>
          </div>
          <div v-else class="finance-order-detail finance-order-detail--hint">
            展开后自动加载详情
          </div>
        </template>
      </a-table>
    </div>

    <pagination
      v-show="pagination.total_count > 0"
      :total-count="pagination.total_count"
      :page.sync="query.page"
      :per-page.sync="query.per_page"
      @change="fetchData"
    />

    <!-- 采购项目详情模态框：只给“项目编号(item_no)”提供可点击入口 -->
    <show-agent-purchase-item
      v-if="isShowPurchaseItemModal"
      :visible.sync="isShowPurchaseItemModal"
      :id="showingPurchaseItemId"
    />
  </div>
</template>

<script>
import { hasPermission } from '@/utils/permission'
import SearchFinanceOrders from '@/views/order_summary/SearchFinanceOrders'
import Pagination from '@/components/Pagination'
import FinanceOrdersBatchStatus from '@/views/order_summary/FinanceOrdersBatchStatus'
import {
  findOrderSummaryFinanceOrders,
  getOrderSummaryFinanceOrderDetail,
  exportOrderSummaryFinanceOrders
} from '@/api/order_summary'
import { exportExcel } from '@/api/excel'
import { formatCurrency } from '@/utils/filter'

// 与后端 GET .../finance_orders/:id/detail 返回的 attributes 键一致；文案与顺序仅前端维护
const FINANCE_ORDER_DETAIL_FIELDS = {
  agent_purchase_item: [
    { key: 'item_no', label: '项目编号' },
    { key: 'carrier_type', label: '运营商类型' },
    { key: 'user_business_type', label: '业务模式' },
    { key: 'agents_product_name', label: '客户套餐' },
    { key: 'service_period', label: '服务周期(月)' },
    { key: 'product_count', label: '开卡数量' },
    { key: 'distributor_price', label: '套餐单价(元)', format: 'currency' },
    { key: 'card_price_display', label: '卡板价格' },
    { key: 'product_fee', label: '套餐费用(元)', format: 'currency' },
    { key: 'card_fee', label: '卡板费用(元)', format: 'currency' },
    { key: 'shipping_fee', label: '快递费(元)', format: 'currency' },
    { key: 'total_fee', label: '项目订单金额(元)', format: 'currency' },
    { key: 'remark', label: '备注' }
  ],
  agent_module_purchase_item: [
    { key: 'item_no', label: '项目编号' },
    { key: 'agents_product_name', label: '模组' },
    { key: 'product_count', label: '采购数量' },
    { key: 'distributor_price', label: '单价(元)', format: 'currency' },
    { key: 'product_fee', label: '模组费用(元)', format: 'currency' },
    { key: 'shipping_fee', label: '快递费(元)', format: 'currency' },
    { key: 'total_fee', label: '项目订单金额(元)', format: 'currency' },
    { key: 'remark', label: '备注' }
  ],
  agent_cpe_purchase_item: [
    { key: 'item_no', label: '项目编号' },
    { key: 'agents_product_name', label: 'CPE' },
    { key: 'product_count', label: '采购数量' },
    { key: 'distributor_price', label: '单价(元)', format: 'currency' },
    { key: 'product_fee', label: 'CPE费用(元)', format: 'currency' },
    { key: 'shipping_fee', label: '快递费(元)', format: 'currency' },
    { key: 'total_fee', label: '项目订单金额(元)', format: 'currency' },
    { key: 'remark', label: '备注' }
  ],
  agent_renew_order: [
    { key: 'bill_no', label: '账单批次' },
    { key: 'cards_count', label: '卡号数量' },
    { key: 'cycles', label: '续期周期' },
    { key: 'total_fee', label: '账单金额(元)', format: 'currency' },
    { key: 'batch_operation_no', label: '批量操作批次' }
  ],
  agent_active_order: [
    { key: 'bill_no', label: '账单批次' },
    { key: 'cards_count', label: '卡号数量' },
    { key: 'total_fee', label: '账单金额(元)', format: 'currency' },
    { key: 'batch_operation_no', label: '批量操作批次' }
  ],
  agent_subscribe_card_order: [
    { key: 'bill_no', label: '账单批次' },
    { key: 'cards_count', label: '卡号数量' },
    { key: 'cycles', label: '订购周期' },
    { key: 'total_fee', label: '账单金额(元)', format: 'currency' },
    { key: 'batch_operation_no', label: '批量操作批次' }
  ],
  agent_product_usage_order: [
    { key: 'agents_product_name', label: '客户套餐' },
    { key: 'bill_month', label: '账单月份' },
    { key: 'usage_order_status', label: '账单状态' },
    { key: 'capacity', label: '额定容量', format: 'currency' },
    { key: 'used', label: '已用量', format: 'currency' },
    { key: 'exceed_usage', label: '超额用量', format: 'currency' },
    { key: 'exceed_fee', label: '超额费用(元)', format: 'currency' },
    { key: 'total_fee', label: '账单总费用(元)', format: 'currency' }
  ],
  agent_allocation_order: [
    { key: 'bill_no', label: '账单批次' },
    { key: 'cards_count', label: '卡号数量' },
    { key: 'product_fee', label: '套餐费用(元)', format: 'currency' },
    { key: 'card_fee', label: '卡板费用(元)', format: 'currency' },
    { key: 'total_fee', label: '账单金额(元)', format: 'currency' }
  ],
  agent_product_stop_order: [
    { key: 'agents_product_name', label: '客户套餐' },
    { key: 'bill_month', label: '账单月份' },
    { key: 'stop_order_status', label: '账单状态' },
    { key: 'cards_count', label: '停机保号卡量' },
    { key: 'stop_price_display', label: '停机保号单价' },
    { key: 'total_fee', label: '账单金额(元)', format: 'currency' }
  ],
  agent_subscribe_traffic_pool_order: [
    { key: 'bill_no', label: '账单批次' },
    { key: 'traffic_pool_product_name', label: '流量池套餐' },
    { key: 'cycles', label: '订购周期' },
    { key: 'total_fee', label: '账单金额(元)', format: 'currency' },
    { key: 'operator_name', label: '操作人' }
  ]
}

export default {
  name: 'OrderSummaryFinanceOrders',
  components: {
    SearchFinanceOrders,
    Pagination,
    FinanceOrdersBatchStatus,
    ShowAgentPurchaseItem: () => import('@/views/agent_monthly_bills/purchase/Show')
  },
  data() {
    return {
      data: [],
      loading: true,
      pagination: {
        total_count: 0
      },
      query: {},
      selectedRowKeys: [],
      expandedRowKeys: [],
      isShowPurchaseItemModal: false,
      showingPurchaseItemId: 0
    }
  },
  computed: {
    columns() {
      return [
        { title: '客户名称', dataIndex: 'agent_name', width: 160 },
        { title: '订单类型', dataIndex: 'order_type_display', width: 150 },
        { title: '订单月份', dataIndex: 'order_month', width: 88 },
        { title: '批次', dataIndex: 'batch_no', width: 240 },
        {
          title: '金额(元)',
          dataIndex: 'order_fee',
          width: 120,
          align: 'right',
          customRender: (text) => (text != null && text !== '' ? formatCurrency(text) : '')
        },
        { title: '责任销售', dataIndex: 'sale_user_name', width: 120 },
        { title: '回款状态', dataIndex: 'payment_status_display', width: 100 },
        { title: '开票状态', dataIndex: 'invoice_status_display', width: 100 },
        { title: '创建时间', dataIndex: 'created_at', width: 170 }
      ]
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    isShowMenu(menu) {
      return hasPermission(menu)
    },

    visibleDetailRows(detail) {
      if (!detail || !detail.attributes) {
        return []
      }
      const defs = FINANCE_ORDER_DETAIL_FIELDS[detail.order_type]
      if (!defs || !defs.length) {
        return []
      }
      const attrs = detail.attributes
      const rows = []
      for (const def of defs) {
        const raw = attrs[def.key]
        if (raw == null || raw === '') {
          continue
        }
        if (typeof raw === 'string' && raw.trim() === '') {
          continue
        }
        const value = this.formatFinanceDetailValue(raw, def.format)
        if (value === '' || value == null) {
          continue
        }
        const link = this.financeDetailFieldLink(detail, def.key)
        const purchaseItemId = this.financeDetailPurchaseItemId(detail, def.key)
        rows.push({ label: def.label, value, link, purchaseItemId })
      }
      return rows
    },

    financeDetailBillQuery(detail) {
      const bid = detail.agent_monthly_bill_id
      if (bid == null || bid === '' || Number(bid) === 0) {
        return {}
      }
      return { bill_id: bid }
    },

    /**
     * 路由目标与 agent_monthly_bills 各子列表一致；父级客户路由段为 /agents/:id（params 使用 id）。
     */
    financeDetailFieldLink(detail, fieldKey) {
      const agentId = detail.agent_id
      if (!agentId) {
        return null
      }
      const q = this.financeDetailBillQuery(detail)
      const slug = detail.order_type
      const agentParams = { id: agentId }
      switch (slug) {
        case 'agent_renew_order':
          if (fieldKey === 'bill_no' && detail.renew_order_id) {
            return {
              name: 'agent_monthly_bill_renew_order_info',
              params: { ...agentParams, renew_order_id: detail.renew_order_id },
              query: q
            }
          }
          if (fieldKey === 'batch_operation_no' && detail.batch_operation_record_id) {
            return {
              name: 'batch_operation_record_info',
              params: { id: detail.batch_operation_record_id }
            }
          }
          break
        case 'agent_active_order':
          if (fieldKey === 'bill_no' && detail.active_order_id) {
            return {
              name: 'agent_monthly_bill_active_order_info',
              params: { ...agentParams, active_order_id: detail.active_order_id },
              query: q
            }
          }
          if (fieldKey === 'batch_operation_no' && detail.batch_operation_record_id) {
            return {
              name: 'batch_operation_record_info',
              params: { id: detail.batch_operation_record_id }
            }
          }
          break
        case 'agent_subscribe_card_order':
          if (fieldKey === 'bill_no' && detail.subscribe_order_id) {
            return {
              name: 'agent_monthly_bill_subscribe_order_info',
              params: { ...agentParams, subscribe_order_id: detail.subscribe_order_id },
              query: q
            }
          }
          if (fieldKey === 'batch_operation_no' && detail.batch_operation_record_id) {
            return {
              name: 'batch_operation_record_info',
              params: { id: detail.batch_operation_record_id }
            }
          }
          break
        case 'agent_product_usage_order':
          if (fieldKey === 'agents_product_name' && detail.product_usage_order_id) {
            return {
              name: 'agent_monthly_bill_card_use_info',
              params: { ...agentParams, card_use_id: detail.product_usage_order_id },
              query: q
            }
          }
          break
        case 'agent_allocation_order':
          if (fieldKey === 'bill_no' && detail.allocation_batch_operation_id) {
            return {
              name: 'allocation_record_info',
              params: { id: detail.allocation_batch_operation_id }
            }
          }
          break
        case 'agent_product_stop_order':
          if (fieldKey === 'agents_product_name' && detail.product_stop_order_id) {
            return {
              name: 'agent_monthly_bill_stop_info',
              params: { ...agentParams, stop_id: detail.product_stop_order_id },
              query: q
            }
          }
          break
        case 'agent_subscribe_traffic_pool_order':
          if (fieldKey === 'bill_no' && detail.subscribe_traffic_pool_order_id) {
            return {
              name: 'agent_monthly_bill_subscribe_traffic_pool_order_info',
              params: { ...agentParams, subscribe_traffic_pool_order_id: detail.subscribe_traffic_pool_order_id },
              query: q
            }
          }
          if (fieldKey === 'traffic_pool_product_name' && detail.agents_product_id) {
            return {
              name: 'agent_products',
              params: { ...agentParams }
            }
          }
          break
        default:
          break
      }
      return null
    },

    /**
     * 采购项目详情：仅“项目编号(item_no)”可点击（打开采购项目详情模态框）
     */
    financeDetailPurchaseItemId(detail, fieldKey) {
      if (!detail) return 0
      if (!['agent_purchase_item', 'agent_module_purchase_item', 'agent_cpe_purchase_item'].includes(detail.order_type)) return 0
      if (fieldKey !== 'item_no') return 0
      if (!this.isShowMenu('agent_purchase_items/show')) return 0
      return detail.purchase_item_id || 0
    },

    openPurchaseItemDetail(purchaseItemId) {
      if (!purchaseItemId) return
      this.showingPurchaseItemId = purchaseItemId
      this.isShowPurchaseItemModal = true
    },

    formatFinanceDetailValue(raw, format) {
      if (format === 'currency') {
        return formatCurrency(Number(raw))
      }
      if (typeof raw === 'number') {
        return String(raw)
      }
      return String(raw)
    },

    expandIcon(props) {
      const { expanded, onExpand, record } = props
      return (
        <a onClick={e => onExpand(record, e)} class='finance-orders-expand-icon'>
          {expanded ? <a-icon type='minus-square' /> : <a-icon type='plus-square' />}
        </a>
      )
    },

    onExpand(expanded, record) {
      if (expanded) {
        if (!this.expandedRowKeys.includes(record.id)) {
          this.expandedRowKeys = [...this.expandedRowKeys, record.id]
        }
        if (record.detail == null && !record.detailLoading) {
          this.$set(record, 'detailLoading', true)
          getOrderSummaryFinanceOrderDetail(record.id).then((res) => {
            if (res.code === 0) {
              this.$set(record, 'detail', res.data)
            } else {
              this.$message.error(res.msg || '加载详情失败')
            }
            this.$set(record, 'detailLoading', false)
          }).catch(() => {
            this.$set(record, 'detailLoading', false)
            this.$message.error('加载详情失败')
          })
        }
      } else {
        this.expandedRowKeys = this.expandedRowKeys.filter((id) => id !== record.id)
      }
    },

    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys
    },

    submitSearch(search) {
      this.query = Object.assign(
        {},
        this.$options.data().query,
        { per_page: this.query.per_page || 20 },
        search
      )
      this.fetchData()
    },

    fetchData() {
      this.loading = true
      this.selectedRowKeys = []
      this.expandedRowKeys = []
      findOrderSummaryFinanceOrders(this.query).then((res) => {
        if (res.code === 0) {
          this.data = res.data
          this.pagination = res.pagination
        }
        this.loading = false
      })
    },

    exportPayload() {
      const { ...rest } = this.query
      return rest
    },

    exportData() {
      exportOrderSummaryFinanceOrders(this.exportPayload()).then((res) => {
        if (res.code === 0 && res.data.is_direct_download === true) {
          exportExcel(res.data.id, res.data.file_name)
        }
      })
    }
  }
}
</script>

<style scoped>
.finance-orders-table-wrap {
  overflow-x: auto;
}

.finance-orders-expand-icon {
  display: inline-flex;
  align-items: center;
  margin-right: 6px;
  color: #1890ff;
}

/* 展开行与主表同宽，避免固定列叠在展开区域上；展开内容单独一块，不挤压主行各列 */
.finance-order-detail {
  margin: 6px 0 14px 0;
  padding: 14px 18px 14px 18px;
  background: #fafbfd;
  border: 1px solid #e6e8eb;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.finance-order-detail--loading,
.finance-order-detail--hint {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 12px 18px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
}

.finance-order-detail--hint-only {
  border: none;
  box-shadow: none;
  background: transparent;
  padding: 8px 0;
  min-height: auto;
}

.finance-order-detail__loading-text {
  margin-left: 10px;
}

.finance-order-detail__row {
  display: flex;
  align-items: flex-start;
  padding: 9px 0;
  border-bottom: 1px solid #eef0f3;
  font-size: 13px;
  line-height: 1.65;
}

.finance-order-detail__row:last-child {
  border-bottom: none;
  padding-bottom: 2px;
}

.finance-order-detail__row:first-child {
  padding-top: 2px;
}

.finance-order-detail__label {
  flex: 0 0 152px;
  max-width: 40%;
  color: rgba(0, 0, 0, 0.55);
  text-align: right;
  padding-right: 10px;
  box-sizing: border-box;
}

.finance-order-detail__value {
  flex: 1;
  min-width: 0;
  color: rgba(0, 0, 0, 0.88);
  word-break: break-word;
}

.finance-order-detail__link {
  color: #1890ff;
}
</style>
